import {AssetUploadProperty, PropertyDependencies} from "../upload.model";

import {AssetProperties} from "../../assets/asset.model";
import {FormControl, FormGroup, Validators} from "@angular/forms";

/**
 * @return all properties that can actually be used in the form. if sub values are not eligible, they are removed (e.g. multiple select)
 */
export function calculateEligibleFormProperties(uploadProperties: AssetUploadProperty[], chosenProperties: AssetProperties): AssetUploadProperty[] {
    return uploadProperties
        .filter(property => satisfyDependencies(property.dependencies || {}, chosenProperties))
        .map(property => buildFormProperty(property, chosenProperties))
        .filter(formProperty => !!formProperty)
}

function satisfyDependencies(dependencies: PropertyDependencies, chosenProperties: AssetProperties) {
    if (Object.keys(dependencies).length === 0) {
        return true; // no dependencies, always eligible
    }
    // otherwise must have at least one dependency satisfied
    for (let dependency in dependencies) {
        let value = chosenProperties[dependency];
        let dependencyValues = dependencies[dependency] || [];
        if (Array.isArray(value) && value.some(v => dependencyValues.includes(v))) {
            return true;
        }
    }
    return false;
}

function buildFormProperty(property: AssetUploadProperty, chosenProperties: AssetProperties): AssetUploadProperty | null {
    if (property.type === 'checkbox') {
        let eligibleValues = property.values
            .filter(val => !val.isReadOnly) // remove readonly values completely for now
            .filter(val => satisfyDependencies(val.dependencies || {}, chosenProperties));
        if (eligibleValues.length === 0) {
            return null;
        }
        return {...property, values: eligibleValues};
    }
    return property;
}

/**
 * @param formProperties the form properties to be used in the form.
 * @param chosenValues the values of the asset that are already set
 */
export function buildUploadPropertiesForm(formProperties: AssetUploadProperty[], chosenValues: AssetProperties): FormGroup {
    let formGroup = new FormGroup({});
    for (let property of formProperties) {
        formGroup.addControl(property.name, buildFormControl(property, chosenValues));
    }
    return formGroup;
}

function buildFormControl(property: AssetUploadProperty, chosenValues: AssetProperties): FormControl {
    let chosenValue = chosenValues[property.name];
    let validators = [];
    if (property.validation?.required || (property.validation?.minSize || 0) > 0) {
        validators.push(Validators.required);
    }
    switch (property.type) {
        case 'text':
        case "textarea":
            if (property.validation?.maxLength) {
                validators.push(Validators.maxLength(property.validation.maxLength));
            }
            return new FormControl({value: chosenValue, disabled: property.isReadonly}, {
                validators: property.validation?.required ? [Validators.required] : null,
                updateOn: 'blur'
            });
        case 'date':
            return new FormControl({value: chosenValue, disabled: property.isReadonly});
        case 'checkbox':
            if (property.validation?.minSize) {
                validators.push(Validators.minLength(property.validation.minSize));
            }
            let values: string[] = [];
            if (Array.isArray(chosenValue)) {
                let eligibleValues: string[] = property.values.map(v => v.value);
                values = chosenValue.filter(v => eligibleValues.includes(v));
            }
            return new FormControl({value: values, disabled: property.isReadonly}, {validators});
        case 'boolean':
            return new FormControl({value: chosenValue, disabled: property.isReadonly});
    }
}
